import React, {useEffect, useRef, useState} from "react";
import styles from "../../App.module.scss";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import NewName from "../Name/NewName";
import NewPublicationYear from "../PublicationYear/NewPublicationYear";
import EditAreasOfApplication from "../AreaOfApplication/EditAreasOfApplication";
import MarkdownEditor from "../Markdown/MarkdownEditor";
import EditOverviewTable from "../Overview/EditOverviewTable";
import EditPsychometricParameterTable
  from "../PsychometricParameter/EditPsychometricParameterTable";
import PreviousVersionsEdit from "../PreviousVersions/PreviousVersionsEdit";
import DownloadEdit from "../Downloads/DownloadEdit";
import {
  DownloadLink, EvaluationRoutineLink,
  FileMetadata,
  Instrument,
  InstrumentShortVersion,
  PsychometricParameter,
  PsychometricParameterTypePsychometricRecommendationEnum, StudyLinks,
  TagCategory
} from "../../gen/api";
import PsychometricService from "../../services/psychometric.service";
import {useAreasOfApplication, useSelectedTagIds} from "../../util/tags";
import {useDispatch, useSelector} from "react-redux";
import {selectTags} from "../../store/selectors";
import TagTable from "../Tags/TagTable";
import {Stack, Tooltip} from "@mui/material";
import {setErrorMessage} from "../../store/reducers/message";
import EvaluationRoutineEdit from "../EvaluationRoutine/EvaluationRoutineEdit";
import {Save} from "@mui/icons-material";
import PsychometricParameterRecommendation
  from "../PsychometricParameter/PsychometricParameterRecommendation";
import SecondaryHeader from "../Header/SecondaryHeader";
import GridPaper from "../ContentBox/GridPaper";
import StudyLinksEdit from "../StudyLinks/StudyLinksEdit";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import {green, red, yellow} from "@mui/material/colors";

type Props = {
  handleSubmitInstrument: (instrument: Instrument) => void
  handleAbort: () => void
  instrument?: Instrument
}

export default function InstrumentForm({handleSubmitInstrument, handleAbort, instrument}: Props) {
  const [name, setName] = useState<string>("");
  const [lastUpdate, setLastUpdate] = useState<Date>();
  const [publicationYear, setPublicationYear] = useState<string>();
  const [author, setAuthor] = useState<string | undefined>();
  const [formOfSurvey, setFormOfSurvey] = useState<string | undefined>();
  const [targetDimension, setTargetDimension] = useState<string | undefined>();
  const [rater, setRater] = useState<string | undefined>();
  const [ageRange, setAgeRange] = useState<string | undefined>();
  const [items, setItems] = useState<string | undefined>();
  const [processingTime, setProcessingTime] = useState<string | undefined>();
  const [openAccess, setOpenAccess] = useState<string | undefined>();
  const [diagnosticObjectiveOfTheAssessmentInstrument, setDiagnosticObjectiveOfTheAssessmentInstrument] = useState<string | undefined>();
  const [descriptionOfTheAssessmentInstrument, setDescriptionOfTheAssessmentInstrument] = useState<string | undefined>();
  const [notes, setNotes] = useState<string | undefined>();
  const [languageVersions, setLanguageVersions] = useState<string | undefined>();
  const [linkToAssessmentTool, setLinkToAssessmentTool] = useState<string | undefined>();
  const [originalLiterature, setOriginalLiterature] = useState<string | undefined>();
  const [testDescriptionsAndReviews, setTestDescriptionsAndReviews] = useState<string | undefined>();

  const [psychometricParametersValidity, setPsychometricParametersValidity] = useState<PsychometricParameter[]>([]);
  const [psychometricParametersReliability, setPsychometricParametersReliability] = useState<PsychometricParameter[]>([]);
  const [psychometricParametersFairness, setPsychometricParametersFairness] = useState<PsychometricParameter[]>([]);
  const [psychometricParametersNormalization, setPsychometricParametersNormalization] = useState<PsychometricParameter[]>([]);

  const [psychometricIDValidity, setPsychometricIDValidity] = useState<string>("");
  const [psychometricIDReliability, setPsychometricIDReliability] = useState<string>("");
  const [psychometricIDFairness, setPsychometricIDFairness] = useState<string>("");
  const [psychometricIDNormalization, setPsychometricIDNormalization] = useState<string>("");

  const [psychometricRecommendationValidity, setPsychometricRecommendationValidity] = useState<PsychometricParameterTypePsychometricRecommendationEnum>();
  const [psychometricRecommendationReliability, setPsychometricRecommendationReliability] = useState<PsychometricParameterTypePsychometricRecommendationEnum>();
  const [psychometricRecommendationFairness, setPsychometricRecommendationFairness] = useState<PsychometricParameterTypePsychometricRecommendationEnum>();
  const [psychometricRecommendationNormalization, setPsychometricRecommendationNormalization] = useState<PsychometricParameterTypePsychometricRecommendationEnum>();

  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[]>([]);
  const [studyLinks, setStudyLinks] = useState<StudyLinks[]>([]);
  const [forms, setForms] = useState<FileMetadata[]>([]);
  const [evaluationRoutineLinks, setEvaluationRoutineLinks] = useState<EvaluationRoutineLink[]>([]);
  const [evaluationRoutines, setEvaluationRoutines] = useState<FileMetadata[]>([]);

  const [previousInstruments, setPreviousInstruments] = useState<InstrumentShortVersion[]>([]);

  const [formValid, setFormValid] = useState<boolean>(false);

  const [tagCategories, setTagCategories] = useState<TagCategory[]>();
  const areasOfApplication = useAreasOfApplication(tagCategories);
  const selectedTagIds = useSelectedTagIds(tagCategories);
  const rawTagCategories = useSelector(selectTags);
  const dispatch = useDispatch();
  const formInitialised = useRef(false);

  useEffect(() => {
    // effect is triggered twice in dev mode with React.StrictMode
    if (formInitialised.current) return;
    formInitialised.current = true;
    // new Instrument
    if (!instrument) {
      setTagCategories(rawTagCategories.map(category => ({
        ...category,
        tags: category.tags.map(parentTag => ({
          ...parentTag,
          selected: false,
          childTags: parentTag.childTags?.map(childTag => ({...childTag, selected: false})) || []
        }))
      })));

      PsychometricService.getPsychometricParameters().then(response => {
        if (response) {
          response.forEach((psy) => {
            if (psy.name === "Validität") {
              setPsychometricIDValidity(psy.id);
            }
            if (psy.name === "Reliabilität") {
              setPsychometricIDReliability(psy.id);
            }
            if (psy.name === "Fairness") {
              setPsychometricIDFairness(psy.id);
            }
            if (psy.name === "Normierung") {
              setPsychometricIDNormalization(psy.id);
            }
          });
        }
      }).catch(_ => {
        dispatch(setErrorMessage("Die Psychometrischen Kennwerte konnten nicht geladen werden."));
      });

      return;
    }
    setName(instrument.name);
    setLastUpdate(instrument.createTimestamp ? new Date(instrument.lastUpdateTimestamp || instrument.createTimestamp) : undefined);
    setPublicationYear(String(instrument.publicationYear));
    setAuthor(instrument.authors);
    setFormOfSurvey(instrument.formOfSurvey);
    setTargetDimension(instrument.targetDimension);
    setRater(instrument.rater);
    setAgeRange(instrument.ageRange);
    setItems(instrument.items);
    setProcessingTime(instrument.processingTime);
    setOpenAccess(instrument.openAccess);
    setDiagnosticObjectiveOfTheAssessmentInstrument(instrument.diagnosticObjectiveOfTheAssessmentInstrument);
    setDescriptionOfTheAssessmentInstrument(instrument.descriptionOfTheAssessmentInstrument);
    setNotes(instrument.notes);
    setLanguageVersions(instrument.languageVersions);
    setLinkToAssessmentTool(instrument.linkToAssessmentTool);
    setPreviousInstruments(instrument.previousVersions || []);
    setOriginalLiterature(instrument.originalLiterature);
    setTestDescriptionsAndReviews(instrument.testDescriptionsAndReviews);
    setTagCategories(instrument.tagCategories);

    instrument.psychometricParameterTypes?.forEach((psy) => {
      if (psy.name === "Validität") {
        if (psy.psychometricParameters !== undefined) {
          setPsychometricParametersValidity(psy.psychometricParameters);
        }
        setPsychometricRecommendationValidity(psy.psychometricRecommendation);
        setPsychometricIDValidity(psy.id);
      }
      if (psy.name === "Reliabilität") {
        if (psy.psychometricParameters !== undefined) {
          setPsychometricParametersReliability(psy.psychometricParameters);
        }
        setPsychometricRecommendationReliability(psy.psychometricRecommendation);
        setPsychometricIDReliability(psy.id);
      }
      if (psy.name === "Fairness") {
        if (psy.psychometricParameters !== undefined) {
          setPsychometricParametersFairness(psy.psychometricParameters);
        }
        setPsychometricRecommendationFairness(psy.psychometricRecommendation);
        setPsychometricIDFairness(psy.id);
      }
      if (psy.name === "Normierung") {
        if (psy.psychometricParameters !== undefined) {
          setPsychometricParametersNormalization(psy.psychometricParameters);
        }
        setPsychometricRecommendationNormalization(psy.psychometricRecommendation);
        setPsychometricIDNormalization(psy.id);
      }
    });

    setDownloadLinks(instrument.downloadLinks || []);
    setStudyLinks(instrument.studyLinks || []);
    setEvaluationRoutineLinks(instrument.evaluationRoutineLink || []);
    setForms(instrument.forms || []);
    setEvaluationRoutines(instrument.evaluationRoutines || []);
  }, [instrument, rawTagCategories, dispatch]);

  useEffect(() => {
    setFormValid(name.length > 0 && areasOfApplication?.tags.flatMap(parentTag => [...parentTag.childTags || [], parentTag]).some(tag => tag.selected)!!);
  }, [name, areasOfApplication]);

  const handleTagClicked = (tagId: string) => {
    if (!tagCategories) return;
    const updatedTagCategories = [...tagCategories.map(category => {
          const tags = category.tags.map(parentTag => {
                // toggle parentTag and set all childTags
                if (parentTag.id === tagId) {
                  return {
                    ...parentTag,
                    selected: !parentTag.selected,
                    childTags: parentTag.childTags?.map(childTag => ({
                      ...childTag,
                      selected: !parentTag.selected
                    }))
                  };
                } else {
                  const adjustedChildTags = parentTag.childTags?.map(childTag =>
                      // toggle childTag
                      childTag.id === tagId ? {
                        ...childTag,
                        selected: !childTag.selected
                      } : childTag
                  )
                  return {
                    // select/unselect parent if all/not all children are selected
                    ...parentTag,
                    selected: (adjustedChildTags && adjustedChildTags.length > 0) ? adjustedChildTags.every(child => child.selected) : parentTag.selected,
                    childTags: adjustedChildTags
                  }
                }
              }
          )
          return {...category, tags: tags};
        }
    )];
    setTagCategories(updatedTagCategories);
  };

  const handleNewDownloadSubmit = (downloadLink: DownloadLink) => {
    setDownloadLinks([...downloadLinks, downloadLink]);
  }

  const handleNewStudySubmit = (studyLink: StudyLinks) => {
    setStudyLinks([...studyLinks, studyLink]);
  }

  const handleNewFormSubmit = (form: FileMetadata) => {
    setForms([...forms, form]);
  }


  const handleNewEvaluationRoutineLinkSubmit = (evaluationRoutineLink: EvaluationRoutineLink) => {
    setEvaluationRoutineLinks([...evaluationRoutineLinks, evaluationRoutineLink]);
  }

  const handleDeleteEvaluationRoutineLink = (evaluationRoutineLink: EvaluationRoutineLink) => {
    setEvaluationRoutineLinks([...evaluationRoutineLinks.filter(link => link !== evaluationRoutineLink)])
  }
  const handleNewEvaluationRoutineSubmit = (evaluationRoutine: FileMetadata) => {
    setEvaluationRoutines([...evaluationRoutines, evaluationRoutine]);
  }

  const handleDeleteDownloadLink = (downloadLink: DownloadLink) => {
    console.log(downloadLink)
    setDownloadLinks([...downloadLinks.filter(link => link !== downloadLink)])
    console.log(downloadLinks)
  }

  const handleDeleteStudyLink = (studyLink: StudyLinks) => {
    setStudyLinks([...studyLinks.filter(link => link !== studyLink)])
  }

  function handleDeleteForm(formId: string) {
    setForms([...forms.filter(form => form.id !== formId)]);
  }

  function handleDeleteEvaluationRoutine(evaluationRoutineId: string) {
    setEvaluationRoutines([...evaluationRoutines.filter(evaluationRoutine => evaluationRoutine.id !== evaluationRoutineId)]);
  }

  const submitInstrument = () => {

    const psychometricParametersValidityModel = {
      id: psychometricIDValidity,
      name: "Validität",
      psychometricParameters: psychometricParametersValidity,
      psychometricRecommendation: psychometricRecommendationValidity
    }

    const psychometricParametersReliabilityModel = {
      id: psychometricIDReliability,
      name: "Reliabilität",
      psychometricParameters: psychometricParametersReliability,
      psychometricRecommendation: psychometricRecommendationReliability
    }

    const psychometricParametersFairnessModel = {
      id: psychometricIDFairness,
      name: "Fairness",
      psychometricParameters: psychometricParametersFairness,
      psychometricRecommendation: psychometricRecommendationFairness
    }

    const psychometricParametersNormalizationModel = {
      id: psychometricIDNormalization,
      name: "Normierung",
      psychometricParameters: psychometricParametersNormalization,
      psychometricRecommendation: psychometricRecommendationNormalization
    }

    const newPsychometricParameterTypes = [psychometricParametersValidityModel, psychometricParametersReliabilityModel, psychometricParametersFairnessModel, psychometricParametersNormalizationModel];

    const newInstrument: Instrument = {
      tagCategories: tagCategories || [],
      name: name,
      publicationYear: Number(publicationYear),
      authors: author,
      formOfSurvey: formOfSurvey,
      targetDimension: targetDimension,
      rater: rater,
      ageRange: ageRange,
      items: items,
      processingTime: processingTime,
      openAccess: openAccess,
      diagnosticObjectiveOfTheAssessmentInstrument: diagnosticObjectiveOfTheAssessmentInstrument,
      descriptionOfTheAssessmentInstrument: descriptionOfTheAssessmentInstrument,
      notes: notes,
      languageVersions: languageVersions,
      linkToAssessmentTool: linkToAssessmentTool,
      originalLiterature: originalLiterature,
      testDescriptionsAndReviews: testDescriptionsAndReviews,
      psychometricParameterTypes: newPsychometricParameterTypes,
      downloadLinks: downloadLinks,
      studyLinks: studyLinks,
      evaluationRoutineLink: evaluationRoutineLinks,
      forms: forms,
      evaluationRoutines: evaluationRoutines,
      previousVersions: previousInstruments
    };

    handleSubmitInstrument(newInstrument);
  }
  return (
      <>
        <Grid container spacing={1}>
          <SecondaryHeader>
            <Grid item xs={4} md={2}>
              <Button sx={{m: "8px 0"}} variant="contained" onClick={handleAbort}>Abbrechen</Button>
            </Grid>
            <Grid item xs={4} md={8}>
              <h1 className={styles['content-title']}>{name} {lastUpdate ? `(zuletzt geändert am ${lastUpdate.toLocaleString()} Uhr)` : ""}</h1>
            </Grid>
            <Grid item xs={4} md={2} display="flex" justifyContent="flex-end">
              <Tooltip
                  title={formValid ? "" : "Die Pflichtfelder Titel und Einsatzbereiche müssen ausgefüllt sein."}>
                <span>
                    <Button sx={{m: "8px 0"}} variant="contained"
                            onClick={submitInstrument}
                            disabled={!formValid} endIcon={<Save/>}>
                      Speichern
                    </Button>
                </span>
              </Tooltip>
            </Grid>
          </SecondaryHeader>
          <GridPaper xs={6}>
            <Stack spacing={4}>
              <NewName name={name} setName={setName}/>
              <NewPublicationYear publicationYear={publicationYear}
                                  setPublicationYear={setPublicationYear}/>
            </Stack>
          </GridPaper>
          <Grid item xs={6}>
            <EditAreasOfApplication
                areasOfApplication={areasOfApplication}
                handleAreasOfApplicationCheck={handleTagClicked}/>
          </Grid>
          <GridPaper title="Autoren">
            <MarkdownEditor value={author} setValue={setAuthor}/>
          </GridPaper>
          <EditOverviewTable
              formOfSurvey={formOfSurvey} setFormOfSurvey={setFormOfSurvey}
              targetDimension={targetDimension} setTargetDimension={setTargetDimension}
              rater={rater} setRater={setRater}
              ageRange={ageRange} setAgeRange={setAgeRange}
              items={items} setItems={setItems}
              processingTime={processingTime} setProcessingTime={setProcessingTime}
              openAccess={openAccess} setOpenAccess={setOpenAccess}/>
          <GridPaper title="Diagnostische Zielsetzung des Assessmentinstruments">
            <MarkdownEditor value={diagnosticObjectiveOfTheAssessmentInstrument}
                            setValue={setDiagnosticObjectiveOfTheAssessmentInstrument}/>
          </GridPaper>
          <GridPaper title="Beschreibung des Assessmentinstruments">
            <MarkdownEditor value={descriptionOfTheAssessmentInstrument}
                            setValue={setDescriptionOfTheAssessmentInstrument}/>
          </GridPaper>

          <EditPsychometricParameterTable
              type="Validität"
              psychometricParameters={psychometricParametersValidity}
              setPsychometricParameters={setPsychometricParametersValidity}/>
          <PsychometricParameterRecommendation type="Validität"
                                               onChange={setPsychometricRecommendationValidity}
                                               psychometricRecommendation={psychometricRecommendationValidity}/>

          <EditPsychometricParameterTable
              type="Reliabilität"
              psychometricParameters={psychometricParametersReliability}
              setPsychometricParameters={setPsychometricParametersReliability}/>
          <PsychometricParameterRecommendation type="Reliabilität"
                                               onChange={setPsychometricRecommendationReliability}
                                               psychometricRecommendation={psychometricRecommendationReliability}/>

          <EditPsychometricParameterTable
              type="Fairness"
              psychometricParameters={psychometricParametersFairness}
              setPsychometricParameters={setPsychometricParametersFairness}/>
            <GridPaper xs={5} md={3} xl={2}
                       sx={{textAlign: 'center'}}>
            </GridPaper>
          {/*<PsychometricParameterRecommendation type="Fairness"
                                               onChange={setPsychometricRecommendationFairness}
                                               psychometricRecommendation={psychometricRecommendationFairness}/>*/}

          <EditPsychometricParameterTable
              type="Normierung"
              psychometricParameters={psychometricParametersNormalization}
              setPsychometricParameters={setPsychometricParametersNormalization}/>
          <PsychometricParameterRecommendation type="Normierung"
                                               onChange={setPsychometricRecommendationNormalization}
                                               psychometricRecommendation={psychometricRecommendationNormalization}/>

          <GridPaper title="Besonderheiten/Anmerkungen">
            <MarkdownEditor value={notes}
                            setValue={setNotes}/>
          </GridPaper>
          <GridPaper title="Sprachversionen des Assessmentinstruments">
            <MarkdownEditor value={languageVersions} setValue={setLanguageVersions}/>
          </GridPaper>
          <GridPaper title="Link zum Assessmentinstrument">
            <MarkdownEditor value={linkToAssessmentTool}
                            setValue={setLinkToAssessmentTool}/>
          </GridPaper>
          <GridPaper title="Links zu anderen Versionen">
            <PreviousVersionsEdit setPreviousInstruments={setPreviousInstruments}
                                  previousInstruments={previousInstruments}/>
          </GridPaper>
          <GridPaper title="Originalliteratur">
            <MarkdownEditor value={originalLiterature}
                            setValue={setOriginalLiterature}/>
          </GridPaper>
          <GridPaper title="Testbeschreibungen/-rezensionen">
            <MarkdownEditor value={testDescriptionsAndReviews}
                            setValue={setTestDescriptionsAndReviews}/>
          </GridPaper>
          <GridPaper title="Downloads">
            <DownloadEdit downloadLinks={downloadLinks}
                          handleNewDownloadLinkSubmit={handleNewDownloadSubmit}
                          handleDeleteDownloadLink={handleDeleteDownloadLink}
                          forms={forms}
                          handleNewFormSubmit={handleNewFormSubmit}
                          handleDeleteForm={handleDeleteForm}
            />
          </GridPaper>
          <GridPaper title="Auswertungsroutinen">
            <EvaluationRoutineEdit evaluationRoutineLink={evaluationRoutineLinks}
                                   handleNewEvaluationRoutineLinkSubmit={handleNewEvaluationRoutineLinkSubmit}
                                   handleDeleteEvaluationRoutineLink={handleDeleteEvaluationRoutineLink}
                evaluationRoutines={evaluationRoutines}
                handleNewEvaluationRoutineSubmit={handleNewEvaluationRoutineSubmit}
                handleDeleteEvaluationRoutine={handleDeleteEvaluationRoutine}
            />
          </GridPaper>
          <GridPaper title="Links zu Studien der Studienplattform">
            <StudyLinksEdit studyLinks={studyLinks}
                            handleNewStudySubmit={handleNewStudySubmit}
                            handleDeleteStudyLink={handleDeleteStudyLink}
            />
          </GridPaper>
          <GridPaper title="Tags für die Suche">
            {tagCategories !== undefined && selectedTagIds !== undefined &&
                <TagTable tagCategories={tagCategories} handleOnClick={handleTagClicked}
                          selectedFilters={selectedTagIds} sx={{border: "none"}}/>
            }
          </GridPaper>
        </Grid>
      </>
  );
}
